import { lazy, Suspense } from "react";
import { Route, Routes, BrowserRouter, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import Home from "./features/home/Home";
import AppIntro from "./features/intro/AppIntro";
import Layout from "./layouts/Layout";
import ProtectedRoutes from "./apiCalls/jwt/ProtectedRoutes";
import { AuthProvider } from "./apiCalls/jwt/AuthContext";
import { EggRewardHost } from "./components/ui/EggRewardHost";

const Shorts = lazy(() => import("./features/shorts/Shorts"));
const Podcast = lazy(() => import("./features/guides/Podcast"));
const Game = lazy(() => import("./features/game/Game"));
const Story = lazy(() => import("./features/stories/Story"));
const StoriesViewer = lazy(() => import("./features/stories/StoriesViewer"));
const Puzzle = lazy(() => import("./features/puzzle/Puzzle"));
const GAFps = lazy(() => import("./features/play/GAFps"));
const Play = lazy(() => import("./features/play/Play"));
const Profile = lazy(() => import("./features/profile/Profile"));
const SubmitRedeem = lazy(() => import("./features/rewards/SubmitRedeem"));
const ThankYou = lazy(() => import("./features/rewards/Thankyou"));
const Unsub = lazy(() => import("./features/legal/unsub"));
const Terms = lazy(() => import("./features/legal/Terms"));
const NewsDetails = lazy(() => import("./features/news/NewsDetails"));
const ArticleView = lazy(() => import("./features/news/ArticleView"));
const Rewards = lazy(() => import("./features/rewards/Rewards"));
const TodaysChallenges = lazy(() => import("./features/play/TodaysChallenges"));

function RouteFallback() {
  return (
    <div
      style={{
        minHeight: "50vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0D0F0A",
        color: "#9AA88A",
        fontFamily: "Poppins, sans-serif",
        fontSize: 13,
      }}
    >
      Loading...
    </div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="sync">
      <Routes location={location}>
        <Route
          path="/intro"
          element={<ProtectedRoutes element={AppIntro} requireAuth={false} />}
        />
        <Route
          path="/"
          element={<ProtectedRoutes element={Home} requireAuth={false} />}
        />
        <Route
          path="/home"
          element={<ProtectedRoutes element={Home} requireAuth={false} />}
        />
        <Route path="/bites" element={<ProtectedRoutes element={Shorts} />} />
        <Route path="/guides" element={<ProtectedRoutes element={Podcast} />} />
        <Route path="/game" element={<ProtectedRoutes element={Game} />} />
        <Route path="/shorts" element={<Navigate to="/game" replace />} />
        <Route path="/profile" element={<ProtectedRoutes element={Profile} />} />
        <Route path="/tips" element={<ProtectedRoutes element={Story} />} />
        <Route
          path="/stories"
          element={<ProtectedRoutes element={StoriesViewer} />}
        />
        <Route path="/puzzle" element={<ProtectedRoutes element={Puzzle} />} />
        <Route path="/play" element={<ProtectedRoutes element={Play} />} />
        <Route
          path="/ga-fps-game"
          element={<ProtectedRoutes element={GAFps} />}
        />
        <Route
          path="/submit-redeem"
          element={<ProtectedRoutes element={SubmitRedeem} />}
        />
        <Route path="/rewards" element={<ProtectedRoutes element={Rewards} />} />
        <Route path="/thankyou" element={<ProtectedRoutes element={ThankYou} />} />
        <Route path="/unsubscribe" element={<Unsub />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/challenges" element={<ProtectedRoutes element={TodaysChallenges} />} />
        <Route path="/news" element={<ProtectedRoutes element={NewsDetails} />} />
        <Route
          path="/news/:gameName"
          element={<ProtectedRoutes element={NewsDetails} />}
        />
        <Route
          path="/article-view"
          element={<ProtectedRoutes element={ArticleView} />}
        />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <EggRewardHost />
        <Layout>
          <Suspense fallback={<RouteFallback />}>
            <AnimatedRoutes />
          </Suspense>
        </Layout>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
